import { $themeBreakpoints } from '@themeConfig'

const safeStorageGet = (key, fallback) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return fallback
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw)
  } catch (e) {
    return fallback
  }
}

const safeStorageSet = (key, value) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    // noop
  }
}

const STORAGE_KEYS = {
  activeMenu: 'prep_budget_active_menu_v2',
  menuSet: 'prep_budget_menu_set_v1',
  mainAddOptions: 'prep_budget_main_add_options_v1',
}

const DEFAULT_MAIN_ADD_OPTIONS = [
  { value: 'zsaluzas-allvanyozas', text: 'Zsaluzás és állványozás' },
  { value: 'sikalapozas', text: 'Sikalapozás' },
  { value: 'melyalapozas', text: 'Mély alapozás' },
  { value: 'helyszini-beton-vasbeton', text: 'Helyszini beton és vasbeton' },
  { value: 'egyeb', text: 'Egyébb' },
  { value: 'uj', text: 'Új' },
]

const initialActiveMenu = safeStorageGet(STORAGE_KEYS.activeMenu, '')
const initialMenuSet = safeStorageGet(STORAGE_KEYS.menuSet, 'default')
const initialMainAddOptions = safeStorageGet(STORAGE_KEYS.mainAddOptions, DEFAULT_MAIN_ADD_OPTIONS)

export default {
  namespaced: true,
  state: {
    windowWidth: 0,
    shallShowOverlay: false,
    prepBudgetMode: false,
    prepBudgetTitle: '',
    prepBudgetDynamicLevels: [],
    prepBudgetActiveMenuKey: initialActiveMenu || '',
    prepBudgetBackRoute: null,
    prepBudgetMenuSet: initialMenuSet || 'default',
    prepBudgetOfferType: '',
    prepBudgetOfferId: null,
    prepBudgetAddModalOpen: false,
    prepBudgetAddModalMode: 'main',
    prepBudgetCustomMenus: [],
    prepBudgetMainAddOptions: Array.isArray(initialMainAddOptions) && initialMainAddOptions.length
      ? initialMainAddOptions
      : DEFAULT_MAIN_ADD_OPTIONS,
  },
  getters: {
    currentBreakPoint: state => {
      const { windowWidth } = state
      if (windowWidth >= $themeBreakpoints.xl) return 'xl'
      if (windowWidth >= $themeBreakpoints.lg) return 'lg'
      if (windowWidth >= $themeBreakpoints.md) return 'md'
      if (windowWidth >= $themeBreakpoints.sm) return 'sm'
      return 'xs'
    },
  },
  mutations: {
    UPDATE_WINDOW_WIDTH(state, val) {
      state.windowWidth = val
    },
    TOGGLE_OVERLAY(state, val) {
      state.shallShowOverlay = val !== undefined ? val : !state.shallShowOverlay
    },
    SET_PREP_BUDGET_MODE(state, val) {
      state.prepBudgetMode = !!val
    },
    SET_PREP_BUDGET_TITLE(state, val) {
      state.prepBudgetTitle = val || ''
    },
    ADD_PREP_BUDGET_LEVEL(state) {
      const current = Array.isArray(state.prepBudgetDynamicLevels) ? state.prepBudgetDynamicLevels : []
      const maxLevel = current.length ? Math.max(...current) : 1
      const nextLevel = maxLevel + 1
      state.prepBudgetDynamicLevels = [...current, nextLevel]
    },
    REMOVE_PREP_BUDGET_LEVEL(state, level) {
      const current = Array.isArray(state.prepBudgetDynamicLevels) ? state.prepBudgetDynamicLevels : []
      state.prepBudgetDynamicLevels = current.filter(item => item !== level)
    },
    RESET_PREP_BUDGET_LEVELS(state) {
      state.prepBudgetDynamicLevels = []
    },
    SET_PREP_BUDGET_ACTIVE_MENU(state, key) {
      state.prepBudgetActiveMenuKey = key || ''
      safeStorageSet(STORAGE_KEYS.activeMenu, state.prepBudgetActiveMenuKey)
    },
    SET_PREP_BUDGET_BACK_ROUTE(state, route) {
      state.prepBudgetBackRoute = route || null
    },
    SET_PREP_BUDGET_MENU_SET(state, menuSet) {
      state.prepBudgetMenuSet = menuSet || 'default'
      safeStorageSet(STORAGE_KEYS.menuSet, state.prepBudgetMenuSet)
    },
    SET_PREP_BUDGET_CONTEXT(state, payload) {
      const data = payload || {}
      state.prepBudgetOfferType = data.offerType || ''
      state.prepBudgetOfferId = data.offerId || null
    },
    OPEN_PREP_BUDGET_ADD_MODAL(state, mode) {
      state.prepBudgetAddModalOpen = true
      state.prepBudgetAddModalMode = mode || 'main'
    },
    CLOSE_PREP_BUDGET_ADD_MODAL(state) {
      state.prepBudgetAddModalOpen = false
      state.prepBudgetAddModalMode = 'main'
    },
    ADD_PREP_BUDGET_CUSTOM_MENU(state, menu) {
      const current = Array.isArray(state.prepBudgetCustomMenus) ? state.prepBudgetCustomMenus : []
      if (!menu || !menu.key || !menu.title) return
      if (current.some(item => item.key === menu.key)) return
      state.prepBudgetCustomMenus = [...current, {
        ...menu,
        parentKey: menu.parentKey || null,
      }]
    },
    SET_PREP_BUDGET_CUSTOM_MENUS(state, menus) {
      const current = Array.isArray(menus) ? menus : []
      state.prepBudgetCustomMenus = current
    },
    RESET_PREP_BUDGET_CUSTOM_MENUS(state) {
      state.prepBudgetCustomMenus = []
    },
    ADD_PREP_BUDGET_MAIN_ADD_OPTION(state, option) {
      const current = Array.isArray(state.prepBudgetMainAddOptions) ? state.prepBudgetMainAddOptions : []
      if (!option || !option.value || !option.text) return
      const value = String(option.value).trim()
      const text = String(option.text).trim()
      if (!value || !text) return
      if (value === 'uj') return
      if (current.some(item => String(item.value) === value)) return

      const withoutUj = current.filter(item => String(item.value) !== 'uj')
      state.prepBudgetMainAddOptions = [...withoutUj, { value, text }, { value: 'uj', text: 'Új' }]
      safeStorageSet(STORAGE_KEYS.mainAddOptions, state.prepBudgetMainAddOptions)
    },
    SET_PREP_BUDGET_MAIN_ADD_OPTIONS(state, options) {
      const current = Array.isArray(options) ? options : []
      const normalized = current
        .map(item => ({
          value: String(item && item.value ? item.value : '').trim(),
          text: String(item && item.text ? item.text : '').trim(),
        }))
        .filter(item => item.value && item.text && item.value !== 'uj' && item.value !== 'alapozas')

      state.prepBudgetMainAddOptions = [...normalized, { value: 'uj', text: 'Új' }]
      safeStorageSet(STORAGE_KEYS.mainAddOptions, state.prepBudgetMainAddOptions)
    },
    RESET_PREP_BUDGET_MAIN_ADD_OPTIONS(state) {
      state.prepBudgetMainAddOptions = DEFAULT_MAIN_ADD_OPTIONS
      safeStorageSet(STORAGE_KEYS.mainAddOptions, DEFAULT_MAIN_ADD_OPTIONS)
    },
    REMOVE_PREP_BUDGET_CUSTOM_MENU(state, menuKey) {
      const key = String(menuKey || '')
      if (!key) return
      const current = Array.isArray(state.prepBudgetCustomMenus) ? state.prepBudgetCustomMenus : []
      if (!current.length) return

      const byParent = current.reduce((acc, item) => {
        const p = item && item.parentKey ? String(item.parentKey) : ''
        if (!acc[p]) acc[p] = []
        acc[p].push(item && item.key ? String(item.key) : '')
        return acc
      }, {})

      const toRemove = new Set([key])
      const queue = [key]
      while (queue.length) {
        const next = queue.shift()
        const children = byParent[next] || []
        children.forEach(childKey => {
          if (!toRemove.has(childKey)) {
            toRemove.add(childKey)
            queue.push(childKey)
          }
        })
      }

      state.prepBudgetCustomMenus = current.filter(item => !toRemove.has(String(item.key || '')))

      if (toRemove.has(String(state.prepBudgetActiveMenuKey || ''))) {
        state.prepBudgetActiveMenuKey = ''
        safeStorageSet(STORAGE_KEYS.activeMenu, state.prepBudgetActiveMenuKey)
      }
    },
  },
  actions: {},
}
